import { titleCaseWords } from "../strings.js";
import { resolveEntityTemplate, resolveEntityTemplateTab } from "./entity-template.js";
import { isSyntheticEntityTabKey, requireSyntheticEntityTab, resolveSyntheticEntityTabs, } from "./synthetic-tabs.js";
export const ENTITY_AUTHORING_METADATA_TAB_KEY = "entity.metadata";
/** Derived from the synthetic-tab registry; kept as a named export for consumers. */
export const ENTITY_AUTHORING_ATTACHMENTS_TAB_KEY = requireSyntheticEntityTab("attachments").tabKey;
function getAuthoringAttributes(source) {
    return (source?.attributes ??
        source?.compiledCatalog?.attributes ??
        source?.compiledCatalogJson?.attributes ??
        []);
}
function getEntityAuthoringAttributes(source, entityTypeKey) {
    return getAuthoringAttributes(source)
        .filter((attribute) => attribute.entityTypeKey === entityTypeKey && !attribute.disabledAt)
        .sort((left, right) => left.listOrder - right.listOrder);
}
export function findOptionLabel(fieldOptions, value) {
    if (fieldOptions.type === "flat") {
        return fieldOptions.options.find((option) => option.value === value)?.label ?? value;
    }
    for (const group of fieldOptions.groups) {
        const match = group.options.find((option) => option.value === value);
        if (match) {
            return match.label;
        }
    }
    return value;
}
export function flattenMetadataFieldOptions(fieldOptions) {
    if (fieldOptions.type === "flat") {
        return fieldOptions.options;
    }
    return fieldOptions.groups.flatMap((group) => group.options.map((option) => ({
        ...option,
        groupLabel: group.label,
    })));
}
function createEnumField(args) {
    return {
        fieldId: args.fieldId,
        persistenceKey: args.persistenceKey ?? args.fieldId,
        label: args.label,
        fieldKind: "enum",
        persistenceSource: "snapshot",
        nodeType: "atomicField",
        options: flattenMetadataFieldOptions(args.options),
        showOnCreate: args.showOnCreate,
    };
}
function createOwnerField(args) {
    return {
        fieldId: args.fieldId,
        persistenceKey: args.persistenceKey,
        label: args.label,
        fieldKind: "owner",
        persistenceSource: "snapshot",
        nodeType: "atomicField",
        showOnCreate: args.showOnCreate,
    };
}
function createPlainTextField(args) {
    return {
        fieldId: args.fieldId,
        persistenceKey: args.persistenceKey,
        label: args.label,
        fieldKind: "plainText",
        persistenceSource: "snapshot",
        nodeType: "atomicField",
        showOnCreate: args.showOnCreate,
    };
}
export function getEntityAuthoringOwnerMode(input) {
    const ownerFields = getEntityAuthoringOwnerFields(input);
    if (ownerFields.length === 0) {
        return "none";
    }
    if (ownerFields.length === 1) {
        return "single";
    }
    if (ownerFields.length === 2) {
        return "dual";
    }
    throw new Error(`Unsupported owner field count '${ownerFields.length}'.`);
}
export function getEntityAuthoringOwnerFields(input) {
    return getEntityAuthoringAttributes(input, input.entityTypeKey)
        .filter((attribute) => attribute.role === "owner")
        .map((attribute) => ({
        fieldId: attribute.fieldId,
        persistenceKey: attribute.persistenceKey,
        label: attribute.label,
        guidance: attribute.guidance,
        assistantGuidance: attribute.assistantGuidance,
        defaultOnCreate: attribute.defaultOnCreate === "actor" ? "actor" : undefined,
        showOnCreate: attribute.showOnCreate,
    }));
}
export function getEntityAuthoringMetadataFields(input) {
    const ownerFields = getEntityAuthoringOwnerFields(input).map((field) => createOwnerField({
        fieldId: field.fieldId,
        persistenceKey: field.persistenceKey,
        label: field.label,
        showOnCreate: field.showOnCreate,
    }));
    const metadataFields = getEntityAuthoringAttributes(input, input.entityTypeKey)
        .filter((attribute) => attribute.role === "attribute")
        .filter((attribute) => attribute.showInAuthoring !== false)
        .map((field) => field.valueType === "enum" && field.options
        ? createEnumField({
            fieldId: field.fieldId,
            label: field.label,
            options: {
                type: "flat",
                options: field.options.map((option) => ({
                    value: option.value,
                    label: option.label,
                    description: option.description,
                    colorToken: option.colorToken,
                })),
            },
            persistenceKey: field.persistenceKey,
            showOnCreate: field.showOnCreate,
        })
        : createPlainTextField({
            fieldId: field.fieldId,
            persistenceKey: field.persistenceKey,
            label: field.label,
            showOnCreate: field.showOnCreate,
        }));
    return [...ownerFields, ...metadataFields];
}
function mergeMetadataFieldsIntoTab(tab, metadataFields) {
    const existingFieldIds = new Set(tab.sections.map((section) => section.fieldId));
    const missingMetadataFields = metadataFields.filter((field) => !existingFieldIds.has(field.fieldId));
    if (missingMetadataFields.length === 0) {
        return tab;
    }
    return {
        ...tab,
        sections: [...tab.sections, ...missingMetadataFields],
    };
}
function normalizeAuthoringField(field) {
    return {
        fieldId: field.fieldId,
        persistenceKey: field.persistenceKey ?? field.fieldId,
        label: field.label,
        fieldKind: field.fieldKind ?? "richText",
        persistenceSource: field.persistenceSource ?? "document",
        nodeType: field.nodeType,
        required: field.required === true,
        guidance: field.guidance,
        assistantGuidance: field.assistantGuidance,
        expectedMentionTypes: field.expectedMentionTypes,
    };
}
export function deriveEntityAuthoringTabLabel(input) {
    if (input.configJson && typeof input.configJson === "object") {
        const configLabel = input.configJson.label;
        if (typeof configLabel === "string" && configLabel.trim().length > 0) {
            return configLabel.trim();
        }
    }
    if (input.fallbackLabel && input.fallbackLabel.trim().length > 0) {
        return input.fallbackLabel.trim();
    }
    const suffix = input.tabKey.split(".").pop() ?? input.tabKey;
    const spaced = suffix
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .replace(/[_-]+/g, " ")
        .trim();
    return titleCaseWords(spaced || input.tabKey);
}
function getCompiledTemplateTabFields(compiledTemplateJson, entityTypeKey, tabKey) {
    const entityTemplate = resolveEntityTemplate(compiledTemplateJson, entityTypeKey);
    if (!entityTemplate) {
        throw new Error(`Missing compiled template for entity '${entityTypeKey}'.`);
    }
    const tab = resolveEntityTemplateTab(entityTemplate, tabKey);
    if (!tab) {
        throw new Error(`Missing compiled template tab '${tabKey}' for entity '${entityTypeKey}'.`);
    }
    return tab.fields.map((field) => normalizeAuthoringField(field));
}
export function normalizeEntityTabOwnerFieldId(input) {
    const sourceLabel = input.sourceLabel ?? `tab '${input.tabKey}'`;
    const rawOwnerFieldId = input.ownerFieldId;
    if (rawOwnerFieldId !== undefined &&
        rawOwnerFieldId !== null &&
        typeof rawOwnerFieldId !== "string") {
        throw new Error(`${sourceLabel}.ownerFieldId must be a string or null.`);
    }
    const explicitOwnerFieldId = typeof rawOwnerFieldId === "string" && rawOwnerFieldId.trim().length > 0
        ? rawOwnerFieldId.trim()
        : null;
    if (isSyntheticEntityTabKey(input.tabKey)) {
        if (explicitOwnerFieldId) {
            throw new Error(`${sourceLabel}.ownerFieldId cannot be set because generated tabs are generic.`);
        }
        return null;
    }
    const ownerFields = getEntityAuthoringOwnerFields(input);
    if (ownerFields.length === 0) {
        if (explicitOwnerFieldId) {
            throw new Error(`${sourceLabel}.ownerFieldId cannot be set for entities without owner fields.`);
        }
        return null;
    }
    if (explicitOwnerFieldId) {
        if (!ownerFields.some((field) => field.fieldId === explicitOwnerFieldId)) {
            throw new Error(`${sourceLabel}.ownerFieldId '${explicitOwnerFieldId}' is not an owner field for entity '${input.entityTypeKey}'.`);
        }
        return explicitOwnerFieldId;
    }
    if (ownerFields.length === 1) {
        return ownerFields[0].fieldId;
    }
    throw new Error(`${sourceLabel}.ownerFieldId is required for multi-owner rich-text entity '${input.entityTypeKey}'.`);
}
export function resolveEntityTabOwnerField(entityTypeKey, compiledTab, source) {
    if (compiledTab.ownerFieldId === undefined) {
        throw new Error(`Compiled tab '${compiledTab.tabKey}' for entity '${entityTypeKey}' is missing ownerFieldId.`);
    }
    if (isSyntheticEntityTabKey(compiledTab.tabKey)) {
        if (compiledTab.ownerFieldId) {
            throw new Error(`Generated tab '${compiledTab.tabKey}' for entity '${entityTypeKey}' cannot declare ownerFieldId.`);
        }
        return null;
    }
    if (compiledTab.ownerFieldId === null) {
        return null;
    }
    const ownerField = getEntityAuthoringOwnerFields({ ...source, entityTypeKey }).find((field) => field.fieldId === compiledTab.ownerFieldId) ?? null;
    if (!ownerField) {
        throw new Error(`Compiled tab '${compiledTab.tabKey}' for entity '${entityTypeKey}' references unknown ownerFieldId '${compiledTab.ownerFieldId}'.`);
    }
    return ownerField;
}
export function resolveEntityTabOwnerPersistenceKey(entityTypeKey, compiledTab, source) {
    return resolveEntityTabOwnerField(entityTypeKey, compiledTab, source)?.persistenceKey ?? null;
}
function toAuthoringTabDescriptor(args) {
    const compiledSections = getCompiledTemplateTabFields(args.compiledTemplateJson, args.entityTypeKey, args.tab.tabKey);
    if (compiledSections.length === 0) {
        throw new Error(`Compiled tab '${args.tab.tabKey}' for entity '${args.entityTypeKey}' has no fields.`);
    }
    const ownerField = resolveEntityTabOwnerField(args.entityTypeKey, args.tab, args.attributeSource);
    return {
        tabKey: args.tab.tabKey,
        label: deriveEntityAuthoringTabLabel({
            tabKey: args.tab.tabKey,
            configJson: args.tab.configJson,
        }),
        listOrder: args.tab.listOrder,
        ownerFieldId: ownerField?.fieldId ?? null,
        ownerPersistenceKey: ownerField?.persistenceKey ?? null,
        contentSupported: compiledSections.length > 0,
        persistenceSource: "document",
        sections: compiledSections,
    };
}
export function resolveEntityAuthoringTabDescriptors(args) {
    const compiledStructure = args.compiledStructure;
    if (!compiledStructure) {
        throw new Error(`Missing compiled entity view for '${args.entityTypeKey}'.`);
    }
    const entityView = compiledStructure.entityViews.find((view) => view.entityTypeKey === args.entityTypeKey) ?? null;
    if (!entityView) {
        throw new Error(`Missing compiled entity view for '${args.entityTypeKey}'.`);
    }
    const tabs = [...entityView.tabs]
        .sort((left, right) => left.listOrder - right.listOrder)
        .map((tab) => toAuthoringTabDescriptor({
        entityTypeKey: args.entityTypeKey,
        tab,
        compiledTemplateJson: args.compiledTemplateJson,
        attributeSource: compiledStructure,
    }));
    // Generated tabs carry no template fields, so they never contribute sections and are
    // never content-supported — Claudia and Quality Guard read this schema to decide what
    // may be authored.
    for (const definition of resolveSyntheticEntityTabs(entityView)) {
        if (tabs.some((tab) => tab.tabKey === definition.tabKey))
            continue;
        const maxListOrder = tabs.reduce((max, tab) => Math.max(max, tab.listOrder), -1);
        tabs.push({
            tabKey: definition.tabKey,
            label: definition.label,
            listOrder: maxListOrder + 1,
            ownerFieldId: null,
            ownerPersistenceKey: null,
            contentSupported: false,
            persistenceSource: "document",
            sections: [],
        });
    }
    const metadataFields = getEntityAuthoringMetadataFields({
        entityTypeKey: args.entityTypeKey,
        ...compiledStructure,
    });
    const metadataTabIndex = tabs.findIndex((tab) => tab.tabKey === ENTITY_AUTHORING_METADATA_TAB_KEY);
    if (metadataFields.length > 0) {
        if (metadataTabIndex >= 0) {
            tabs[metadataTabIndex] = mergeMetadataFieldsIntoTab(tabs[metadataTabIndex], metadataFields);
        }
        else {
            const maxListOrder = tabs.reduce((max, tab) => Math.max(max, tab.listOrder), -1);
            tabs.push({
                tabKey: ENTITY_AUTHORING_METADATA_TAB_KEY,
                label: "Metadata",
                listOrder: maxListOrder + 1,
                ownerFieldId: null,
                ownerPersistenceKey: null,
                contentSupported: true,
                persistenceSource: "snapshot",
                sections: metadataFields,
            });
        }
    }
    return tabs.sort((left, right) => left.listOrder - right.listOrder);
}
